import { useCallback, useState } from "react";
import PageLayout from "./ui/PageLayout";
import Copyright from "./ui/Copyright";
import { useDropzone } from "react-dropzone";
import { LuUploadCloud } from "react-icons/lu";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Tabs from "./ui/Tabs";
import { useQueryParams } from "../hooks/useQueryParams";
import CandidateRecommendationsTable from "./CandidateRecommendationsTable";
import JobRecommendationsTable from "./JobRecommendationsTable";
import NotMatchedTable from "./NotMatchedTable";
import UploadProgressTracker from "./UploadProgressTracker";
import useFetch from "../hooks/useFetch";
import { useSelector } from "react-redux";
import { selectCid } from "../features/companyid";

const TALENT_POOL_MAIN_TABS = [
    { label: "Spotted", value: "recommended" },
    { label: "Pooled", value: "not-matched" },
];

const RECOMMENDED_SUB_TABS = [
    { label: "By Candidates", value: "candidates" },
    { label: "By Jobs", value: "recommendations" },
];

const TalentPool = () => {
    const { query: urlQuery } = useQueryParams();
    const activeTab = urlQuery.tab || "recommended";
    const activeSubTab = urlQuery.subtab || "candidates";
    const cid = useSelector(selectCid)

    const { data: countsData } = useFetch(
        `/api/v1/internal-sourcing/counts?company_id=${cid}`
    );

    const mainTabs = TALENT_POOL_MAIN_TABS.map(tab => {
        let count = 0;
        if (tab.value === "recommended") {
            count = Math.max(countsData?.recommended_count || 0, countsData?.total_job_candidates || 0);
        } else if (tab.value === "not-matched") {
            count = countsData?.unmatched_count || 0;
        }
        return { ...tab, label: `${tab.label} (${count})` };
    });

    const subTabs = RECOMMENDED_SUB_TABS.map(tab => {
        let count = 0;
        if (tab.value === "candidates") {
            count = countsData?.recommended_count || 0;
        } else if (tab.value === "recommendations") {
            count = countsData?.total_job_candidates || 0;
        }
        return { ...tab, label: `${tab.label} (${count})` };
    });

    const [runId, setRunId] = useState(null);

    const { send: bulkUpload } = useFetch(
        '/api/v1/internal-sourcing-bulk-upload',
        { method: 'POST' },
        false
    );

    const onDrop = useCallback((acceptedFiles, fileRejections) => {
        fileRejections.forEach((rejection) => {
            rejection.errors.forEach((error) => {
                if (error.code === 'file-too-large') {
                    toast.error(`"${rejection.file.name}" is too large. Max size is 25MB.`);
                } else if (error.code === 'file-invalid-type') {
                    toast.error(`"${rejection.file.name}" is not a ZIP file.`);
                } else {
                    toast.error(error.message || 'An error occurred during file selection.');
                }
            });
        });

        if (acceptedFiles.length > 0) {
            const uploadFile = async () => {
                const formData = new FormData();
                formData.append('company_id', cid);

                acceptedFiles.forEach(file => {
                    formData.append('files', file);
                });

                const res = await bulkUpload(formData);
                if (res && res.run_id) {
                    setRunId(res.run_id);
                }
                return res;
            };

            toast.promise(uploadFile(), {
                pending: 'Uploading ZIP file(s)...',
                success: 'Upload accepted, processing started!',
                error: 'Failed to upload ZIP file(s)'
            });
        }
    }, [bulkUpload, cid]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/zip': ['.zip'],
            'application/x-zip-compressed': ['.zip']
        },
        maxSize: 25 * 1024 * 1024, // 25 MB
        multiple: true
    });

    return (
        <PageLayout title={<div>Talent Pool<span className="align-super text-xs ml-2 italic">Beta</span></div>} className={"px-0"}>
            <div
                {...getRootProps()}
                className={`w-full h-[200px] mt-6 border-2 border-dashed rounded-divRadius flex flex-col items-center justify-center cursor-pointer transition-all duration-300 group
                ${isDragActive
                        ? "border-web-blue bg-web-blue/5 scale-[1.01] shadow-sh"
                        : "border-mutedGray bg-white hover:border-web-blue hover:bg-slate-50 shadow-sm"
                    }`}
            >
                <input {...getInputProps()} />
                <div className="bg-web-blue/10 p-5 rounded-full mb-4 group-hover:scale-110 transition-transform duration-300">
                    <LuUploadCloud className="text-web-blue text-4xl" />
                </div>
                <div className="text-center">
                    <p className="text-primaryBlack font-semibold text-lg">
                        {isDragActive ? "Drop the zip files here..." : "Drop Resumes here or click to upload"}
                    </p>
                    <p className="text-textGray text-sm mt-1">
                        Only <span className="font-medium text-web-blue">.zip</span> files are supported. Max size: <span className="font-medium">25MB</span>
                    </p>
                </div>
            </div>

            <div className="mt-8">
                <Tabs
                    tabKey="tab"
                    tabs={mainTabs}
                    className="w-full flex justify-between bg-white"
                    tabClass="w-1/2"
                />
            </div>

            {activeTab === "recommended" && (
                <div className="mt-6">
                    <Tabs
                        tabKey="subtab"
                        tabs={subTabs}
                        className="w-fit bg-white"
                    />
                </div>
            )}

            {activeTab === "recommended" && activeSubTab === "candidates" ? (
                <CandidateRecommendationsTable />
            ) : activeTab === "recommended" && activeSubTab === "recommendations" ? (
                <JobRecommendationsTable />
            ) : (
                <NotMatchedTable />
            )}
            <Copyright />
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
            {runId && (
                <UploadProgressTracker runId={runId} onClose={() => setRunId(null)} />
            )}
        </PageLayout>
    )
}

export default TalentPool